import React, { useEffect, useRef, useState } from 'react';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import 'handsontable/dist/handsontable.full.min.css';
import { db } from '../firebaseConfig';
import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, query, where, orderBy } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import FullScreenButton from './FullScreenButton';

const MIN_ROWS = 10;

const HEADERS = ['운송구간', '차종', '구간', '청구금액'];
const FIELDS = ['route', 'cargo', 'fuelSegment', 'billingAmount'];

interface TruckingRateRow {
  company: string;
  route: string;
  cargo: string;
  fuelSegment: string;
  billingAmount: number;
  order: number; // 내부 정렬용
}

interface TruckingRatePageProps {
  isFullScreen: boolean;
  setIsFullScreen: (v: boolean) => void;
}

const TruckingRatePage: React.FC<TruckingRatePageProps> = ({ isFullScreen, setIsFullScreen }) => {
  const [companyList, setCompanyList] = useState<string[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [tableData, setTableData] = useState<any[][]>([]);
  const hotRef = useRef<any>(null);

  // 거래처 목록 불러오기
  useEffect(() => {
    const fetchCompanies = async () => {
      const colRef = collection(db, 'dispatch_company');
      const querySnap = await getDocs(colRef);
      const companies: string[] = [];
      querySnap.forEach(doc => {
        const data = doc.data();
        if (data.company) companies.push(data.company);
      });
      setCompanyList(companies);
      if (companies.length > 0 && !selectedCompany) setSelectedCompany(companies[0]);
    };
    fetchCompanies();
    // eslint-disable-next-line
  }, []);

  // 운임표 데이터 불러오기
  useEffect(() => {
    if (!selectedCompany) {
      setTableData([]);
      return;
    }
    const fetchRates = async () => {
      const colRef = collection(db, 'TruckingRate');
      const q = query(colRef, where('company', '==', selectedCompany), orderBy('order', 'asc'));
      const querySnap = await getDocs(q);
      const rates: TruckingRateRow[] = [];
      querySnap.forEach(doc => {
        rates.push(doc.data() as TruckingRateRow);
      });
      // 🚩 데이터가 없으면 빈 운임표 생성 및 Firestore에 저장
      if (rates.length === 0) {
        const emptyRows: TruckingRateRow[] = [];
        for (let i = 0; i < MIN_ROWS; i++) {
          const emptyRow: TruckingRateRow = {
            company: selectedCompany,
            route: '',
            cargo: '',
            fuelSegment: '기본',
            billingAmount: 0,
            order: i + 1,
          };
          emptyRows.push(emptyRow);
          await setDoc(doc(db, 'TruckingRate', `${selectedCompany}_${i + 1}`), emptyRow);
        }
        setTableData(emptyRows.map(r => FIELDS.map(f => r[f as keyof TruckingRateRow] ?? '')));
        return;
      }
      // 🚩 기존 데이터 있으면 그대로 로드
      const rows = rates.map((r: TruckingRateRow) => FIELDS.map(f => r[f as keyof TruckingRateRow] ?? ''));
      while (rows.length < MIN_ROWS) {
        rows.push(Array(HEADERS.length).fill(''));
      }
      setTableData(rows);
    };
    fetchRates();
  }, [selectedCompany]);

  // route에서 키워드 추출 함수
  function extractKeywords(route: string) {
    const arr = (route || '')
      .split(/[-,→\s]+/)
      .map(s => s.trim())
      .filter(Boolean);
    if (route && !arr.includes(route)) arr.unshift(route); // route 전체도 포함
    return arr;
  }

  // Firestore에 운임표 1개 저장 (company+order 조합으로 문서ID)
  const saveRate = async (rate: TruckingRateRow) => {
    if (!rate.company) return;
    const docId = `${rate.company}_${rate.order}`;
    // keywords 필드 추가
    const keywords = extractKeywords(rate.route);
    await setDoc(doc(db, 'TruckingRate', docId), { ...rate, keywords });
  };

  // 셀 수정 시 Firestore에 저장
  const handleAfterChange = async (changes: any, source: string) => {
    if (source === 'loadData' || !changes) return;
    let newTable = hotRef.current?.hotInstance?.getData() || tableData;
    for (const [row, col, oldValue, newValue] of changes) {
      if (!selectedCompany) continue;
      // 저장
      const rate: TruckingRateRow = {
        company: selectedCompany,
        route: newTable[row][0] || '',
        cargo: newTable[row][1] || '',
        fuelSegment: newTable[row][2] || '기본',
        billingAmount: Number(newTable[row][3]) || 0,
        order: row + 1,
      };
      await saveRate(rate);
    }
  };

  // 행 삭제 함수 (company+order로 삭제)
  const handleDeleteRow = async (rowIndex: number) => {
    if (!selectedCompany) return;
    const row = tableData[rowIndex];
    const orderVal = row[4] || rowIndex + 1;
    const docId = `${selectedCompany}_${orderVal}`;
    await deleteDoc(doc(db, 'TruckingRate', docId));
    setTableData(prev => prev.filter((_, idx) => idx !== rowIndex));
  };

  // order 재정렬 함수 (삭제/추가 후)
  const reorderRows = async (rows: any[][]) => {
    const newRows = rows.map((r, idx) => {
      r[4] = idx + 1;
      return r;
    });
    setTableData(newRows);
    // Firestore에도 반영
    for (const r of newRows) {
      const rate: TruckingRateRow = {
        company: selectedCompany,
        route: r[0] || '',
        cargo: r[1] || '',
        fuelSegment: r[2] || '기본',
        billingAmount: Number(r[3]) || 0,
        order: r[4] || 0,
      };
      await saveRate(rate);
    }
  };

  // 행 추가 함수
  const handleAddRow = () => {
    const newRow = Array(HEADERS.length + 1).fill(''); // +1 for order
    newRow[4] = tableData.length + 1;
    const newTable = [...tableData, newRow];
    reorderRows(newTable);
    setTimeout(() => {
      if (hotRef.current) {
        const row = tableData.length;
        hotRef.current.hotInstance.selectCell(row, 0);
      }
    }, 100);
  };

  // 거래처 변경 시
  const handleCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCompany(e.target.value);
  };

  // 엑셀 다운로드 함수
  const handleExcelDownload = () => {
    const exportHeaders = HEADERS.slice(0, -1); // 정렬값 제외
    const exportData = tableData.map(row => row.slice(0, -1));
    // 1. 첫 행: 거래처상호 운임표
    const title = [`${selectedCompany} 운임표`];
    // 2. 두 번째 행: 헤더
    const wsData = [title, exportHeaders, ...exportData];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: exportHeaders.length - 1 } }];
    // 중앙정렬 스타일 추가
    if (!ws['A1'].s) ws['A1'].s = {};
    ws['A1'].s.alignment = { horizontal: 'center', vertical: 'center' };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '운임표');
    XLSX.writeFile(wb, `${selectedCompany}_운임표.xlsx`);
  };

  return (
    <div
      tabIndex={0}
      onClick={() => {
        if (hotRef.current) {
          hotRef.current.hotInstance.listen();
        }
      }}
      style={{ width: '100%', maxWidth: 1200, margin: '0 auto', padding: 32, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 48px)', overflow: 'hidden', boxSizing: 'border-box' }}
    >
      {/* 헤더 영역 */}
      <div style={{ flex: '0 0 auto' }}>
        <h2 style={{ textAlign: 'center', marginBottom: 16, color: '#1976d2' }}>운임표 관리</h2>
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontWeight: 600 }}>거래처상호</span>
          <select value={selectedCompany} onChange={handleCompanyChange} style={{ fontSize: 16, padding: '4px 12px', borderRadius: 4 }}>
            {companyList.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <button onClick={handleAddRow} style={{ marginLeft: 16, padding: '6px 18px', borderRadius: 4, border: 'none', background: '#1976d2', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>+ 행추가</button>
          {/* 엑셀 다운로드 */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button onClick={handleExcelDownload} style={{ padding: '6px 16px', borderRadius: 4, border: 'none', background: '#388e3c', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>엑셀</button>
          </div>
        </div>
      </div>

      {/* 표 영역: 내부 스크롤 */}
      <div style={{ flex: '1 1 0', minHeight: 0, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
        <HotTable
          ref={hotRef}
          data={tableData}
          colHeaders={HEADERS}
          rowHeaders={true}
          width="100%"
          height="100%"
          licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
          manualRowResize={true}
          manualColumnResize={true}
          autoColumnSize={true}
          autoRowSize={false}
          className="htCenter"
          afterChange={handleAfterChange}
          readOnly={false}
          contextMenu={{
            items: {
              'remove_row': {
                name: '삭제',
                callback: function(key: string, selection: any) {
                  const rowIndex = selection[0].start.row;
                  handleDeleteRow(rowIndex);
                }
              }
            }
          }}
          copyPaste={true}
          beforePaste={() => {
            if (hotRef.current) hotRef.current.hotInstance.listen();
          }}
          stretchH="all"
          columns={[
            {}, // 운송구간
            {}, // 차종
            {}, // 구간
            { type: 'numeric', numericFormat: { pattern: '#,##0' } }, // 청구금액
          ]}
        />
      </div>

      {/* 오른쪽 상단 전체화면/원래대로 버튼 */}
      <FullScreenButton isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
    </div>
  );
};

export default TruckingRatePage; 